import { to_markdown } from "jira2md";
import { Integrations } from "src/search/lib/useIntegrations";

/**
 * Converts Jira-formatted text to standard markdown, handling various Jira-specific markup patterns.
 *
 * Handles:
 * - Color markup {color:#hex}text{color}
 * - Bullet points (** and •)
 * - Headers (h1. h2. etc)
 * - Smart links [title|url|smart-link]
 * - Code blocks {code} and {noformat}
 * - Mixed HTML content (<em>, <code>, etc)
 * - Nested markup (color inside lists, code inside color blocks, etc)
 * - Tables and other complex structures
 * - Pre-formatted text
 * - Emphasis (_text_ and *text*)
 *
 * @param description - The Jira-formatted text to convert
 * @param integration - The integration type (to verify it's Jira)
 * @returns Converted markdown text, or original text if not from Jira
 */
export function processJiraMarkdown(
  description: string | undefined,
  integration: string | undefined
): string {
  if (!description || integration !== Integrations.JIRA) {
    return description || "";
  }

  // Helper function to process nested markup within a block of text
  const processNestedMarkup = (text: string): string => {
    return (
      text
        // Handle inline code blocks
        .replace(/<code>(.*?)<\/code>/g, "`$1`")
        // Handle emphasis
        .replace(/<em>(.*?)<\/em>/g, "*$1*")
        // Handle strong
        .replace(/<strong>(.*?)<\/strong>/g, "**$1**")
        // Handle inline color markup
        .replace(/{color:#[\dA-Fa-f]{6}}(.*?){color}/g, "$1")
        // Handle inline links
        .replace(/\[(.*?)\|(.*?)\|smart-link]/g, "[$1]($2)")
        // Handle other inline formatting
        .replace(/_([^_]+)_/g, "*$1*")
        .replace(/\*([^*]+)\*/g, "**$1**")
    );
  };

  // Preprocess the description to handle mixed content
  const preprocessed = description
    // Normalize newlines and whitespace
    .replace(/\r\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")

    // Handle HTML list items with nested content
    .replace(/<li>([^]*?)<\/li>/g, (_match: string, content: string) => {
      // Process the content inside the li tag
      const processedContent = processNestedMarkup(content);
      // Convert to markdown list item
      return `* ${processedContent.trim()}\n`;
    })

    // Handle pre-formatted blocks
    .replace(
      /<pre>([^]*?)<\/pre>/g,
      (_match: string, content: string) => `\`\`\`\n${content.trim()}\n\`\`\``
    )

    // Handle code blocks with language specification
    .replace(
      /{code:([a-z]+)}([^]*?){code}/g,
      (_match: string, lang: string, content: string) =>
        `\`\`\`${lang}\n${content.trim()}\n\`\`\``
    )

    // Handle regular code blocks
    .replace(
      /{code}([^]*?){code}/g,
      (_match: string, content: string) => `\`\`\`\n${content.trim()}\n\`\`\``
    )

    // Handle noformat blocks
    .replace(
      /{noformat}([^]*?){noformat}/g,
      (_match: string, content: string) => `\`\`\`\n${content.trim()}\n\`\`\``
    )

    // Handle tables
    .replace(/{table}([^]*?){table}/g, (_match: string, content: string) => {
      const rows = content.trim().split("\n");
      return rows
        .map(
          (row) =>
            `| ${row
              .split("|")
              .map((cell) => cell.trim())
              .join(" | ")} |`
        )
        .join("\n");
    })

    // Handle Jira bullet points, ensuring they work with nested content
    .replace(
      /^\*\*\s*(.*?)$/gm,
      (_match: string, content: string) =>
        `* ${processNestedMarkup(content.trim())}`
    )

    // First pass: Handle headers with nested markup
    .replace(
      /^h([1-6])\.\s*(.*?)$/gm,
      (_match: string, level: string, content: string) =>
        `${"#".repeat(Number(level))} ${processNestedMarkup(content.trim())}`
    )

    // Handle remaining nested markup in regular paragraphs
    .replace(/^(?![\s#*>|-])(.*?)$/gm, (_match: string, content: string) =>
      processNestedMarkup(content)
    )

    // Clean up any remaining HTML tags
    .replace(/<\/?[a-z]+[^>]*>/g, "")

    // Fix spacing around block elements
    .replace(/(\n{2,})/g, "\n\n")
    .trim();

  return to_markdown(preprocessed);
}

/**
 * Utility function to escape markdown special characters in text
 * Useful when processing text that should be treated as literal
 */
function escapeMarkdown(text: string): string {
  return text.replace(/([!#()*+.[\\\]_`{}\-])/g, "\\$1");
}
